/**
 * ViewportManager.ts — Responsive viewport detection and sizing.
 *
 * Provides viewport class detection and recommended sizing for ASCII art,
 * sprites, and UI elements based on available terminal rows.
 *
 * Viewport Classes:
 * - large (≥30 rows): Full experience, ~1024px+ height
 * - medium (24-29 rows): Standard experience, ~768-1024px
 * - small (18-23 rows): Compact experience, ~600-768px
 * - tiny (<18 rows): Minimal experience, <600px (warning shown)
 */

import type { Terminal } from './Terminal';

export type ViewportClass = 'large' | 'medium' | 'small' | 'tiny';

export interface ViewportSizing {
  /** Maximum rows for ASCII art */
  artHeight: number;
  /** Maximum rows for sprites */
  spriteHeight: number;
  /** Show full frames or compact borders */
  compactFrames: boolean;
  /** Show viewport warning */
  showWarning: boolean;
}

export class ViewportManager {
  private terminal: Terminal;

  constructor(terminal: Terminal) {
    this.terminal = terminal;
  }

  /** Get the current viewport class based on terminal rows */
  getViewportClass(): ViewportClass {
    const { rows } = this.terminal.getSize();
    if (rows >= 30) return 'large';
    if (rows >= 24) return 'medium';
    if (rows >= 18) return 'small';
    return 'tiny';
  }

  /** Get recommended maximum height for ASCII art */
  getArtHeight(): number {
    const vp = this.getViewportClass();
    return {
      large: 12,
      medium: 8,
      small: 5,
      tiny: 3,
    }[vp];
  }

  /** Get recommended maximum height for sprites */
  getSpriteHeight(): number {
    const vp = this.getViewportClass();
    return {
      large: 14,
      medium: 10,
      small: 6,
      tiny: 4,
    }[vp];
  }

  /** Get comprehensive viewport sizing recommendations */
  getSizing(): ViewportSizing {
    const vp = this.getViewportClass();
    return {
      artHeight: this.getArtHeight(),
      spriteHeight: this.getSpriteHeight(),
      compactFrames: vp === 'tiny' || vp === 'small',
      showWarning: vp === 'tiny',
    };
  }

  /** Check if viewport is critically small (should show warning) */
  isCriticallySmall(): boolean {
    const { rows, cols } = this.terminal.getSize();
    return rows < 18 || cols < 60;
  }

  /** Show viewport warning message if critically small */
  showViewportWarning(): void {
    if (!this.isCriticallySmall()) return;

    const { rows, cols } = this.terminal.getSize();
    this.terminal.writeLine(
      `⚠️  Small viewport detected (${cols}×${rows}). Recommended: 80×24 or larger.`,
      'yellow'
    );
    this.terminal.blankLine();
  }
}
